import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { BasicScrollView } from "@/components/BasicScrollView";
import { Pressable, ScrollView, StyleSheet, TextInput as RNTextInput, View } from 'react-native';
import { ArrowIcon, Button as Button95, Hourglass, NumberInput, Panel, TextInput } from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';
import Head from 'expo-router/head';
import { CustomText } from '@/components/CustomText';
import Footer from '@/components/Footer';
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from '@/utilities/AppUtils';
import * as AppConstants from "@/utilities/AppConstants";
import * as AppConsts from "@/utilities/AppConsts";
import { useForm, Controller } from "react-hook-form";
import { AppContext } from '@/components/AppContext';
import { CommunityMembership } from "@/types/community_membership";
import { RelativePathString, useRouter } from 'expo-router';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setData } from '@/state/user';
import { Picker } from '@react-native-picker/picker';
import { CustomDivider } from '@/components/CustomDivider';
import { Meta } from '@/components/Meta';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 10,
    gap: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  formElement: {
    width: AppConsts.BASIC_MINIMUM_WIDTH,
  },
  buttonWrapper: {
    flexDirection: 'row',
  },
  panel: {
    padding: 20,
    gap: 20,
    alignItems: 'center',
    minWidth: AppConsts.BASIC_MINIMUM_WIDTH,
    maxWidth: AppConsts.MAX_WIDTH_COMFORTABLE_READING,
  },
  numericWrapper: {
    flexDirection: 'row',
    gap: 5,
    alignItems: "center",
  },
  numeric: {
    width: 80,
  },
  inputWrapper: {
    gap: 10,
    alignItems: 'center',
  },
  picker: {
    backgroundColor: 'transparent',
    paddingHorizontal: 10,
    paddingVertical: 5,
    width: AppConsts.BASIC_MINIMUM_WIDTH,
  },
  pickerWrapper: {
    borderWidth: 1,
    borderColor: "#636363",
    marginTop: 10,
  },
});

const minYear = (new Date().getFullYear() - 100);
const maxYear = (new Date().getFullYear() - 18);

function getVal(val: string, increment: boolean): string {
  let result = parseInt(val);
  if (Number.isNaN(result)) {
    result = maxYear;
  } else {
    if (increment) {
      result++;
    } else {
      result--;
    }
  }
  return '' + result;
}

function isValidNumber(val: string): boolean {
  const result = parseFloat(val);
  return !Number.isNaN(result);
}

function getCurrentVal(val: string): number {
  let result = parseFloat(val);
  if (Number.isNaN(result)) {
    result = maxYear;
  }
  return result;
}

export default function Age() {
  const domain = useSelector((state: RootState) => state.user.domain);
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const monthInput = useRef<any>(null);
  const router = useRouter();
  const dispatch = useDispatch();

  const { register, control, handleSubmit, setValue, reset, watch, formState: { errors } } = useForm({
    defaultValues: {
      month: '',
      day: '',
      year: '',
    }
  });
  const [month, day, year] = watch(["month", "day", "year"]);

  const onSubmit = useCallback(async (data: any) => {
    try {
      showSpinner();

      const ageInYears = AppUtils.getAgeYears(data.year, data.month, data.day);
      if (ageInYears < 18) {
        throw new Error("You must be at least 18 years old to join Rabbl");
      }

      const response = await AppUtilities.fetchAuthorized(
        "Age.onSubmit",
        "/rabbl/api/set_age",
        "POST",
        {
          domain: domain,
          age_month: data.month,
          age_year: data.year,
          age_day: data.day,
        }
      );
      const membership: CommunityMembership = ((response as any).body as any).community_membership;
      dispatch(setData(response));

      router.navigate(AppUtils.getProfileCreateNextPageLink(membership) as RelativePathString);

    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(data), true);
      AppUtilities.handleException(e, "Age.onSubmit");
    } finally {
      hideSpinner();
    }
  }, [domain]);

  async function onValidationErrors(errors: any) {
    AppUtils.showErrorToast('There are some errors. Review and try again.');
  };

  useEffect(() => {
    if (AppUtilities.shouldAutoFocus()) {
      if (monthInput && monthInput.current) {
        monthInput.current?.focus();
      }
    }
  }, [monthInput]);

  const ageYears = AppUtils.getAgeYears(year, month, day);

  return (
    <BasicScrollView center={true}>
      <Meta title={"Your age"} />
            
      <Panel variant='raised' elevation={4}>
        <View style={styles.panel}>
          <Controller
            control={control}
            name="month"
            rules={{
              validate: (x) => {
                if (x == '') {
                  return 'Birthday month is required.';
                }
                return undefined;
              }
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <View style={styles.inputWrapper}>
                <CustomText>Birthday</CustomText>
                <View style={styles.pickerWrapper}>
                  <Picker selectedValue={value} onValueChange={onChange} style={styles.picker}>
                    <Picker.Item label="Month" value="" />
                    <Picker.Item label="January" value="1" />
                    <Picker.Item label="February" value="2" />
                    <Picker.Item label="March" value="3" />
                    <Picker.Item label="April" value="4" />
                    <Picker.Item label="May" value="5" />
                    <Picker.Item label="June" value="6" />
                    <Picker.Item label="July" value="7" />
                    <Picker.Item label="August" value="8" />
                    <Picker.Item label="September" value="9" />
                    <Picker.Item label="October" value="10" />
                    <Picker.Item label="November" value="11" />
                    <Picker.Item label="December" value="12" />
                  </Picker>
                </View>
              </View>
            )}
          />
          {errors.month && <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>^ {errors.month.message} ^</AppText>}

          <Controller
            control={control}
            name="day"
            rules={{
              validate: (x) => {
                if (x == '') {
                  return 'Birthday day is required.';
                }
                return undefined;
              }
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <View style={styles.inputWrapper}>
                <View style={styles.pickerWrapper}>
                  <Picker selectedValue={value} onValueChange={onChange} style={styles.picker}>
                    <Picker.Item label="Day" value="" />
                    <Picker.Item label="1" value="1" />
                    <Picker.Item label="2" value="2" />
                    <Picker.Item label="3" value="3" />
                    <Picker.Item label="4" value="4" />
                    <Picker.Item label="5" value="5" />
                    <Picker.Item label="6" value="6" />
                    <Picker.Item label="7" value="7" />
                    <Picker.Item label="8" value="8" />
                    <Picker.Item label="9" value="9" />
                    <Picker.Item label="10" value="10" />
                    <Picker.Item label="11" value="11" />
                    <Picker.Item label="12" value="12" />
                    <Picker.Item label="13" value="13" />
                    <Picker.Item label="14" value="14" />
                    <Picker.Item label="15" value="15" />
                    <Picker.Item label="16" value="16" />
                    <Picker.Item label="17" value="17" />
                    <Picker.Item label="18" value="18" />
                    <Picker.Item label="19" value="19" />
                    <Picker.Item label="20" value="20" />
                    <Picker.Item label="21" value="21" />
                    <Picker.Item label="22" value="22" />
                    <Picker.Item label="23" value="23" />
                    <Picker.Item label="24" value="24" />
                    <Picker.Item label="25" value="25" />
                    <Picker.Item label="26" value="26" />
                    <Picker.Item label="27" value="27" />
                    <Picker.Item label="28" value="28" />
                    <Picker.Item label="29" value="29" />
                    <Picker.Item label="30" value="30" />
                    <Picker.Item label="31" value="31" />
                  </Picker>
                </View>
              </View>
            )}
          />
          {errors.day && <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>^ {errors.day.message} ^</AppText>}

          <Controller
            control={control}
            name="year"
            rules={{
              validate: (x) => {
                const i = parseInt(x);
                if (x == '') {
                  return 'Birthday year is required.';
                } else if (Number.isNaN(i)) {
                  return 'Invalid year';
                } else if (i < minYear) {
                  return 'You can\'t be that old :)'
                } else if (i > maxYear) {
                  return 'You must be at least 18 years old.'
                }
                return undefined;
              }
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <View style={styles.inputWrapper}>
                <CustomText>Year</CustomText>
                <View style={styles.numericWrapper}>
                  <Button95
                    disabled={isValidNumber(value) && getCurrentVal(value) <= minYear}
                    onPress={() => onChange(getVal(value, false))}
                  >
                    <ArrowIcon
                      segments={4}
                      disabled={isValidNumber(value) && getCurrentVal(value) <= minYear}
                      direction='left'
                    />
                  </Button95>
                  <TextInput
                    value={value}
                    onChangeText={onChange}
                    autoComplete='birthdate-year'
                    inputMode='numeric'
                    keyboardType='numeric'
                    onSubmitEditing={handleSubmit(onSubmit, onValidationErrors)}
                    placeholder='Year'
                    textAlign='center'
                    style={styles.numeric}
                  />
                  <Button95
                    disabled={isValidNumber(value) && getCurrentVal(value) >= maxYear}
                    onPress={() => onChange(getVal(value, true))}
                  >
                    <ArrowIcon
                      segments={4}
                      disabled={isValidNumber(value) && getCurrentVal(value) >= maxYear}
                      direction='right'
                    />
                  </Button95>
                </View>
              </View>
            )}
          />
          {errors.year && <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>^ {errors.year.message} ^</AppText>}

          <CustomDivider fullWidth={true} />

          {
            !Number.isNaN(ageYears) && (
              <AppText>Age: {ageYears} years old</AppText>
            )
          }

          <View style={styles.buttonWrapper}>
            <Button95
              onPress={handleSubmit(onSubmit, onValidationErrors)}
              disabled={month == '' || day == '' || year == ''}
            >Next</Button95>
          </View>
        </View>
      </Panel>
      
      <Footer />
    </BasicScrollView>
  );
}
